import type {
  ConflictDisplay,
  SimilarityDisplay,
  CoverageGapDisplay,
  StatementDisplay,
} from '../gateway-service';
import type { InteractionLabelsService } from './interaction-labels';

/** Structured, render-agnostic message for one interaction. */
export interface InteractionMessage {
  headline: string;
  qualifier?: string;
  subtitle?: string;
  detail?: string;
}

export type TranslateFn = (
  key: string,
  params?: Record<string, string | number>
) => string;

/** Resolve a constant identifier to display text. */
export type ResolveLabel = (group: string, id: string) => string;

export interface MessageContext {
  resolveLabel: ResolveLabel;
  t: TranslateFn;
  statements: StatementDisplay[];
}

/** Gateway label-group name -> i18n nested-key segment. */
const I18N_SEGMENT: Record<string, string> = {
  interaction_type: 'interactionType',
  defeat_type: 'defeatType',
  resolution_maxim: 'resolutionMaxim',
};

/**
 * Build a label resolver implementing the order: i18n (`relations.<segment>.<id>`)
 * -> gateway-provided label -> raw identifier. `t` returns the key unchanged
 * when a translation is missing, which is how we detect an i18n miss.
 */
export function createResolveLabel(
  t: TranslateFn,
  labels: Pick<InteractionLabelsService, 'getGatewayLabel'>
): ResolveLabel {
  return (group, id) => {
    if (!id) return '';
    const segment = I18N_SEGMENT[group] ?? group;
    const key = `relations.${segment}.${id}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
    return labels.getGatewayLabel(group, id) ?? id;
  };
}

function sentenceFor(
  iri: string | null | undefined,
  statements: StatementDisplay[]
): string | undefined {
  if (!iri) return undefined;
  return statements.find((s) => s.iri === iri)?.source_sentence;
}

function headline(
  label: string,
  subject: string | null | undefined,
  property: string | null | undefined,
  key: string,
  t: TranslateFn
): string {
  if (subject && property) {
    return t(key, { label, subject, property });
  }
  return label;
}

export function buildConflictMessage(
  c: ConflictDisplay,
  ctx: MessageContext
): InteractionMessage {
  const label = ctx.resolveLabel('interaction_type', c.interaction_type);
  const msg: InteractionMessage = {
    headline: headline(
      label,
      c.subject,
      c.property,
      'relations.conflict.headline',
      ctx.t
    ),
  };

  if (c.defeat_type) {
    msg.qualifier = ctx.t('relations.conflict.defeatQualifier', {
      label: ctx.resolveLabel('defeat_type', c.defeat_type),
    });
  }

  if (c.antinomy_scope) {
    msg.subtitle = ctx.t('relations.conflict.scope', {
      scope: c.antinomy_scope,
    });
  }

  if (c.resolved_by && c.prevailing_norm) {
    const maxim = ctx.resolveLabel('resolution_maxim', c.resolved_by);
    const norm =
      sentenceFor(c.prevailing_norm, ctx.statements) ?? c.prevailing_norm;
    msg.detail = ctx.t('relations.conflict.resolved', { maxim, norm });
  } else if (c.resolvable_by && c.resolvable_by.length > 0) {
    const maxim = c.resolvable_by
      .map((m) => ctx.resolveLabel('resolution_maxim', m))
      .join(', ');
    msg.detail = ctx.t('relations.conflict.resolvableBy', { maxim });
  } else if (c.explanation) {
    msg.detail = c.explanation;
  }

  return msg;
}

export function buildSimilarityMessage(
  s: SimilarityDisplay,
  ctx: MessageContext
): InteractionMessage {
  const label = ctx.resolveLabel('interaction_type', s.interaction_type);
  const msg: InteractionMessage = {
    headline: headline(
      label,
      s.subject,
      s.property,
      'relations.similarityMsg.headline',
      ctx.t
    ),
  };
  if (s.explanation) msg.detail = s.explanation;
  return msg;
}

export function buildGapMessage(
  g: CoverageGapDisplay,
  ctx: MessageContext
): InteractionMessage {
  const label = ctx.resolveLabel('interaction_type', g.interaction_type);
  const detail = g.explanation ?? sentenceFor(g.rule, ctx.statements);
  const msg: InteractionMessage = { headline: label };
  if (detail) msg.detail = detail;
  return msg;
}
